import { useEffect, useRef, useState } from "react";
import { Check, Copy, Loader2, Palette, Pipette, X } from "lucide-react";

// Renk kartelasi gorseli — TARAYICIDA KODLA cizilir:
// tek renkli tasarim her renk secenegine boyanir, altina/ustune renk adi
// yazilir. AI kullanilmaz: renk adlari ve tonlar birebir dogru olur.

export interface ChartColor { name: string; hex?: string; /** Hazir tasarim (boyanmaz, oldugu gibi) */ art?: HTMLCanvasElement }

/** Hex kodu + kopyala dugmesi */
export function HexCopy({ hex }: { hex?: string | null }) {
  const [copied, setCopied] = useState(false);
  if (!hex) return null;
  const up = hex.toUpperCase();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(up).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1200); });
      }}
      title="Hex kodunu kopyala"
      className="inline-flex items-center gap-0.5 rounded bg-gray-100 px-1 font-mono text-[10px] text-gray-600 hover:bg-gray-200"
    >
      {up}{copied ? <Check className="h-2.5 w-2.5 text-emerald-600" /> : <Copy className="h-2.5 w-2.5" />}
    </button>
  );
}


/** Yaygin baski (DTF/vinil/serigrafi) renk adlarinin tonlari. AI'in gorselden
 *  TAHMIN ettigi hex guvenilir degil (NAVY siyaha yakin cikiyordu); bilinen
 *  adlarda bu tablo gecerli. Bilinmeyen adda AI tahmini kalir; damlalikla
 *  kaynaktan birebir alinabilir. */
export const INK_PALETTE: Record<string, string> = {
  "BLACK": "#161616", "WHITE": "#ffffff", "NAVY": "#1f2d5c", "ROYAL BLUE": "#2f4fb5", "ROYAL": "#2f4fb5",
  "BLUE JEAN": "#6c86ba", "LIGHT BLUE": "#9cc3e6", "SKY BLUE": "#7fb8e6", "CAROLINA BLUE": "#7bafd4",
  "BABY BLUE": "#a9cdee", "TURQUOISE": "#1fb3b3", "DEEP TEAL": "#1f7d8a", "TEAL": "#11808a",
  "FOREST GREEN": "#1f4a33", "IRISH GREEN": "#44b06a", "KELLY GREEN": "#1f9a4a", "GREEN": "#3fae49",
  "LIME": "#8cc63f", "LIME GREEN": "#8cc63f", "MINT": "#a8e2c6", "SAGE": "#9aab8a", "OLIVE": "#6b6b3a",
  "MILITARY GREEN": "#5a5f3b", "RED": "#e2262d", "MAROON": "#7a1a22", "BURGUNDY": "#6d1a2a",
  "CARDINAL": "#8c1d2c", "CRIMSON": "#a8203a", "ORANGE": "#f5822a", "BURNT ORANGE": "#cc5a1e",
  "RUST": "#b04a22", "CORAL": "#f37b6c", "YELLOW": "#ffeb14", "GOLD": "#c9a43c", "OLD GOLD": "#b99a4b",
  "MUSTARD": "#d4a52a", "BROWN": "#7a4a24", "TAN": "#d2b48c", "BEIGE": "#efe4cf", "CREAM": "#f3ead7",
  "IVORY": "#f5efdf", "SAND": "#d8c7a3", "GRAY": "#8c8c8c", "GREY": "#8c8c8c", "LIGHT GRAY": "#c9c9c9",
  "LIGHT GREY": "#c9c9c9", "CHARCOAL": "#3d3d3f", "SILVER": "#bfbfbf", "PURPLE": "#6a35a0",
  "LAVENDER": "#b9a3dc", "VIOLET": "#8a6bd1", "PINK": "#f39cc4", "LIGHT PINK": "#f7c6d9",
  "HOT PINK": "#e8418a", "FUCHSIA": "#d6338a", "MAGENTA": "#c2257d",
};
export function inkHex(name: string, fallback?: string): string | undefined {
  return INK_PALETTE[name.trim().toUpperCase().replace(/\s+/g, " ")] ?? fallback;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("Tasarım yüklenemedi"));
    im.src = src;
  });
}

/** Seffaf alanlari kirpip tasarimin dolu kismini dondur (bosluk birakmasin) */
function trimmed(im: HTMLImageElement): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = im.naturalWidth; c.height = im.naturalHeight;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(im, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
  let x0 = width, y0 = height, x1 = 0, y1 = 0;
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      if (data[(y * width + x) * 4 + 3] > 20) {
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
      }
    }
  }
  if (x1 <= x0 || y1 <= y0) return c;
  const out = document.createElement("canvas");
  out.width = x1 - x0 + 1; out.height = y1 - y0 + 1;
  out.getContext("2d")!.drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

/** Tonlari koruyarak boya (sunucudaki tintDesignBuffer ile ayni kural):
 *  en koyu kisim hedef renk, acik kisimlar hedefin acik tonu. */
function tinted(src: HTMLCanvasElement, hex: string): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.width; c.height = src.height;
  const ctx = c.getContext("2d")!;
  ctx.drawImage(src, 0, 0);
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return c;
  const [tr, tg, tb] = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
  const img = ctx.getImageData(0, 0, c.width, c.height);
  const d = img.data;
  const hist = new Uint32Array(256);
  let visible = 0;
  for (let o = 0; o < d.length; o += 4) {
    if (d[o + 3] < 40) continue;
    hist[Math.round(0.2126 * d[o] + 0.7152 * d[o + 1] + 0.0722 * d[o + 2])]++; visible++;
  }
  let lo = 0, hi = 255, acc = 0;
  for (let v = 0; v < 256; v++) { acc += hist[v]; if (acc >= visible * 0.02) { lo = v; break; } }
  acc = 0;
  for (let v = 255; v >= 0; v--) { acc += hist[v]; if (acc >= visible * 0.02) { hi = v; break; } }
  const span = hi - lo;
  for (let o = 0; o < d.length; o += 4) {
    const l = 0.2126 * d[o] + 0.7152 * d[o + 1] + 0.0722 * d[o + 2];
    const t = span < 25 ? 0 : Math.min(1, Math.max(0, (l - lo) / span)) * 0.78;
    d[o] = tr + (255 - tr) * t; d[o + 1] = tg + (255 - tg) * t; d[o + 2] = tb + (255 - tb) * t;
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/** Hazir stiller. "shop": magazanin kendi gorselleriyle ayni dil (sicak
 *  bej-seftali gradyan, kalin koyu baslik, adlar ogenin ALTINDA). Kaynak
 *  listenin zemini/basligi KOPYALANMAZ. */
export interface ChartStyle {
  key: string; label: string; title: string;
  bgTop: string; bgBottom: string; ink: string;
  labelBelow: boolean;
}
export const CHART_STYLES: ChartStyle[] = [
  { key: "shop", label: "Mağaza stili (öneri)", title: "PRINT COLORS", bgTop: "#d9d3ce", bgBottom: "#f2d5bd", ink: "#111111", labelBelow: true },
  { key: "clean", label: "Sade beyaz", title: "INK COLORS", bgTop: "#ffffff", bgBottom: "#f4f4f5", ink: "#18181b", labelBelow: true },
  { key: "dark", label: "Koyu", title: "PRINT COLORS", bgTop: "#26272b", bgBottom: "#3a3b40", ink: "#fafafa", labelBelow: true },
];

const FONT = "Poppins, Montserrat, 'Segoe UI', Arial, sans-serif";

export function drawColorChart(opts: {
  canvas: HTMLCanvasElement;
  design: HTMLCanvasElement;
  colors: ChartColor[];
  title: string;
  style: ChartStyle;
  columns: number;
}) {
  const { canvas, design, colors, title, style, columns } = opts;
  const W = 2000;
  const pad = 70;
  const titleH = title ? 190 : 40;
  const cellW = (W - pad * 2) / columns;
  const artH = cellW * Math.min(0.6, design.height / design.width);
  const labelH = 70;
  const cellH = labelH + artH + 40;
  const rows = Math.ceil(colors.length / columns);
  canvas.width = W;
  canvas.height = Math.round(titleH + rows * cellH + pad);
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 0, canvas.width * 0.3, canvas.height);
  grad.addColorStop(0, style.bgTop);
  grad.addColorStop(1, style.bgBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = style.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (title) {
    ctx.font = `800 104px ${FONT}`;
    ctx.fillText(title.toUpperCase(), W / 2, 105);
  }

  colors.forEach((c, i) => {
    const col = i % columns;
    const row = Math.floor(i / columns);
    // Son satir ortalanir (magaza gorsellerindeki gibi)
    const inRow = row === rows - 1 ? colors.length - row * columns : columns;
    const offset = ((columns - inRow) * cellW) / 2;
    const cx = pad + offset + col * cellW + cellW / 2;
    const top = titleH + row * cellH;
    const art = c.art ?? tinted(design, c.hex || "#888888");
    const maxW = cellW * 0.86;
    const scale = Math.min(maxW / art.width, artH / art.height);
    const w = art.width * scale, h = art.height * scale;
    const artTop = style.labelBelow ? top + 10 : top + labelH;
    ctx.drawImage(art, cx - w / 2, artTop + (artH - h) / 2, w, h);
    ctx.fillStyle = style.ink;
    ctx.font = `500 40px ${FONT}`;
    ctx.fillText(c.name.toUpperCase(), cx, style.labelBelow ? artTop + artH + labelH / 2 : top + labelH / 2);
  });
}


/** Kucuk kopya: matriste onlarca hucre boyanacagi icin tam boy gerekmez */
function shrink(src: HTMLCanvasElement, max = 700): HTMLCanvasElement {
  const s = Math.min(1, max / Math.max(src.width, src.height));
  if (s >= 1) return src;
  const c = document.createElement("canvas");
  c.width = Math.round(src.width * s); c.height = Math.round(src.height * s);
  c.getContext("2d")!.drawImage(src, 0, 0, c.width, c.height);
  return c;
}

/** TASARIM x RENK kartelasi. layout "matrix": sutunlar renk, satirlar tasarim;
 *  "rows": her satir bir renk (solda ad), yaninda o renkteki tum tasarimlar. */
export function drawMatrixChart(opts: {
  canvas: HTMLCanvasElement;
  designs: HTMLCanvasElement[];
  colors: ChartColor[];
  title: string;
  style: ChartStyle;
  layout: "matrix" | "rows";
  showHex: boolean;
  tint: (designIndex: number, hex: string) => HTMLCanvasElement;
}) {
  const { canvas, designs, colors, title, style, layout, showHex, tint } = opts;
  const W = 2000, pad = 60;
  const titleH = title ? 170 : 40;
  const aspect = Math.min(0.75, Math.max(0.3, ...designs.map((d) => d.height / Math.max(1, d.width))));
  const ctx0 = () => canvas.getContext("2d")!;
  const swatch = (ctx: CanvasRenderingContext2D, x: number, y: number, hex: string) => {
    ctx.beginPath(); ctx.arc(x, y, 16, 0, Math.PI * 2);
    ctx.fillStyle = hex; ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = "rgba(0,0,0,0.25)"; ctx.stroke();
  };

  if (layout === "matrix") {
    const cols = Math.max(1, colors.length);
    const cellW = (W - pad * 2) / cols;
    const headerH = 80 + (showHex ? 36 : 0);
    const artW = cellW * 0.86, artH = artW * aspect;
    const rowH = artH + 28;
    canvas.width = W;
    canvas.height = Math.round(titleH + headerH + designs.length * rowH + pad);
    const ctx = ctx0();
    const grad = ctx.createLinearGradient(0, 0, W * 0.3, canvas.height);
    grad.addColorStop(0, style.bgTop); grad.addColorStop(1, style.bgBottom);
    ctx.fillStyle = grad; ctx.fillRect(0, 0, W, canvas.height);
    ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillStyle = style.ink;
    if (title) { ctx.font = `800 100px ${FONT}`; ctx.fillText(title.toUpperCase(), W / 2, 100); }
    colors.forEach((c, ci) => {
      const cx = pad + ci * cellW + cellW / 2;
      const hy = titleH + 30;
      ctx.font = `600 34px ${FONT}`;
      const label = c.name.toUpperCase();
      const tw = ctx.measureText(label).width;
      swatch(ctx, cx - tw / 2 - 26, hy, c.hex || "#888888");
      ctx.fillStyle = style.ink; ctx.fillText(label, cx + 10, hy);
      if (showHex && c.hex) { ctx.font = `400 26px ${FONT}`; ctx.fillText(c.hex.toUpperCase(), cx, hy + 40); }
      designs.forEach((_, di) => {
        const art = tint(di, c.hex || "#888888");
        const s = Math.min(artW / art.width, artH / art.height);
        const w = art.width * s, h = art.height * s;
        const top = titleH + headerH + di * rowH;
        ctx.drawImage(art, cx - w / 2, top + (artH - h) / 2, w, h);
      });
    });
    return;
  }

  // "rows": her renk bir satir
  const labelW = 330;
  const cellW = (W - pad * 2 - labelW) / Math.max(1, designs.length);
  const artW = cellW * 0.9, artH = artW * aspect;
  const rowH = Math.max(artH, 90) + 30;
  canvas.width = W;
  canvas.height = Math.round(titleH + colors.length * rowH + pad);
  const ctx = ctx0();
  const grad = ctx.createLinearGradient(0, 0, W * 0.3, canvas.height);
  grad.addColorStop(0, style.bgTop); grad.addColorStop(1, style.bgBottom);
  ctx.fillStyle = grad; ctx.fillRect(0, 0, W, canvas.height);
  ctx.fillStyle = style.ink; ctx.textBaseline = "middle";
  if (title) { ctx.textAlign = "center"; ctx.font = `800 100px ${FONT}`; ctx.fillText(title.toUpperCase(), W / 2, 100); }
  colors.forEach((c, ri) => {
    const top = titleH + ri * rowH;
    const cy = top + rowH / 2 - 10;
    swatch(ctx, pad + 18, cy, c.hex || "#888888");
    ctx.textAlign = "left"; ctx.fillStyle = style.ink;
    ctx.font = `600 34px ${FONT}`; ctx.fillText(c.name.toUpperCase(), pad + 48, showHex && c.hex ? cy - 16 : cy);
    if (showHex && c.hex) { ctx.font = `400 24px ${FONT}`; ctx.fillText(c.hex.toUpperCase(), pad + 48, cy + 20); }
    designs.forEach((_, di) => {
      const art = tint(di, c.hex || "#888888");
      const s = Math.min(artW / art.width, artH / art.height);
      const w = art.width * s, h = art.height * s;
      const cx = pad + labelW + di * cellW + cellW / 2;
      ctx.drawImage(art, cx - w / 2, top + (rowH - 30 - h) / 2, w, h);
    });
  });
}

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

export function ColorChartBuilder({ designUrl, colors: initialColors, onSave, saving, referenceImages = [], onClose, designs = [], matrixColors: initialMatrix = [], onMatrixColorsChange }: {
  designUrl: string;
  /** Tasarim x renk kartelasinin baslangic renkleri (tasarimlarin baski renkleri + kayitli) */
  matrixColors?: ChartColor[];
  /** Matris renk listesi degisince (analize kaydedilir) */
  onMatrixColorsChange?: (colors: ChartColor[]) => void;
  /** Olusturulan tasarimlar: kartela bunlarin KENDISIYLE de kurulabilir */
  designs?: Array<{ id: string; url: string; label: string }>;
  colors: ChartColor[];
  /** Bir ya da birden fazla sayfa (tasarim x renk cok renkte bolunur) */
  onSave: (dataUrls: string[]) => void;
  saving: boolean;
  /** Kaynak gorseller (kartela dahil) — damlalikla renk almak icin gosterilir */
  referenceImages?: string[];
  onClose: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Bilinen adlarda tablo tonu, digerlerinde AI tahmini
  const normalize = (list: ChartColor[]) => list.map((c) => ({ ...c, hex: inkHex(c.name, c.hex) }));
  const [colors, setColors] = useState<ChartColor[]>(() => normalize(initialColors));
  useEffect(() => { setColors(normalize(initialColors)); }, [initialColors]);
  const [refIdx, setRefIdx] = useState(referenceImages.length > 1 ? 1 : 0);
  const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
  const pickFromScreen = async (i: number) => {
    if (!EyeDropper) return;
    try {
      const r = await new EyeDropper().open();
      setColors((p) => p.map((x, j) => (j === i ? { ...x, hex: r.sRGBHex } : x)));
    } catch { /* kullanici vazgecti */ }
  };
  const [style, setStyle] = useState<ChartStyle>(CHART_STYLES[0]);
  const [title, setTitle] = useState(CHART_STYLES[0].title);
  const [columns, setColumns] = useState(initialColors.length > 16 ? 5 : 4);
  const [design, setDesign] = useState<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDesign(null); setError(null);
    loadImage(designUrl).then((im) => setDesign(trimmed(im))).catch((e) => setError(String(e.message ?? e)));
  }, [designUrl]);

  // Kaynak: renk secenekleri (tasarim boyanir) ya da olusturulan tasarimlar (oldugu gibi)
  const [source, setSource] = useState<"colors" | "designs" | "matrix">(designs.length > 1 ? "matrix" : initialColors.length ? "colors" : "designs");
  // Tasarim x renk
  const [mColors, setMColors] = useState<ChartColor[]>(() => initialMatrix.map((c) => ({ ...c, hex: inkHex(c.name, c.hex) })));
  const [layout, setLayout] = useState<"matrix" | "rows">("matrix");
  const [perPage, setPerPage] = useState(6);
  const [showHex, setShowHex] = useState(false);
  const [page, setPage] = useState(0);
  const [newColor, setNewColor] = useState({ name: "", hex: "#2f4fb5" });
  const updateM = (fn: (p: ChartColor[]) => ChartColor[]) => setMColors((p) => { const n = fn(p); onMatrixColorsChange?.(n.map(({ name, hex }) => ({ name, hex }))); return n; });
  const pages: ChartColor[][] = [];
  for (let i = 0; i < mColors.length; i += Math.max(1, perPage)) pages.push(mColors.slice(i, i + Math.max(1, perPage)));
  const tintCache = useRef(new Map<string, HTMLCanvasElement>());
  const [picked, setPicked] = useState<Array<{ id: string; label: string }>>(() => designs.map((d) => ({ id: d.id, label: d.label })));
  const [designArts, setDesignArts] = useState<Record<string, HTMLCanvasElement>>({});
  useEffect(() => {
    let off = false;
    for (const d of designs) {
      if (designArts[d.id]) continue;
      loadImage(d.url).then((im) => { if (!off) setDesignArts((m) => ({ ...m, [d.id]: trimmed(im) })); }).catch(() => { /* yuklenemedi */ });
    }
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designs.map((d) => d.id).join(",")]);
  const entries: ChartColor[] = source === "designs"
    ? picked.filter((p) => designArts[p.id] && p.label.trim()).map((p) => ({ name: p.label, art: designArts[p.id] }))
    : colors.filter((c) => c.name.trim());

  const matrixArts = picked.map((p) => designArts[p.id]).filter((a): a is HTMLCanvasElement => Boolean(a)).map((a) => shrink(a));
  const matrixKey = picked.map((p) => p.id).join(",");
  const tintFor = (di: number, hex: string) => {
    const k = `${matrixKey}|${di}|${hex}|${matrixArts.length}`;
    let c = tintCache.current.get(k);
    if (!c) { c = tinted(matrixArts[di], hex); tintCache.current.set(k, c); }
    return c;
  };
  const pageTitle = (i: number) => (pages.length > 1 ? `${title} ${i + 1}/${pages.length}` : title);
  const drawPage = (canvas: HTMLCanvasElement, i: number) =>
    drawMatrixChart({ canvas, designs: matrixArts, colors: pages[i] ?? [], title: pageTitle(i), style, layout, showHex, tint: tintFor });

  useEffect(() => {
    if (!canvasRef.current) return;
    if (source === "matrix") {
      if (matrixArts.length === 0 || pages.length === 0) return;
      drawPage(canvasRef.current, Math.min(page, pages.length - 1));
      return;
    }
    const base = design ?? (source === "designs" ? entries[0]?.art ?? null : null);
    if (!base) return;
    drawColorChart({ canvas: canvasRef.current, design: base, colors: entries, title, style, columns });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design, colors, title, style, columns, source, picked, designArts, mColors, layout, perPage, showHex, page]);

  const saveAll = () => {
    if (source !== "matrix") { if (canvasRef.current) onSave([canvasRef.current.toDataURL("image/jpeg", 0.92)]); return; }
    const urls = pages.map((_, i) => { const c = document.createElement("canvas"); drawPage(c, i); return c.toDataURL("image/jpeg", 0.92); });
    onSave(urls);
  };

  return (
    <div className="fixed inset-0 z-[70] flex bg-black/60 p-3" onClick={onClose}>
    <div className="flex min-h-0 w-full flex-col overflow-hidden rounded-xl bg-white text-xs shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
          <Palette className="h-4 w-4 text-amber-600" /> Renk kartelası
          <span className="text-xs font-normal text-gray-500">— mağaza stilinde; tasarım her renge boyanır (tek renkli tasarımlar için)</span>
        </p>
        <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
      </div>
      {error && <p className="px-4 pt-2 text-red-600">{error}</p>}
      <div className="flex min-h-0 flex-1">
        {/* Sol: BUYUK onizleme */}
        <div className="flex min-w-0 flex-1 items-center justify-center overflow-auto bg-gray-100 p-4">
          {design || (source === "designs" && entries.length > 0) || (source === "matrix" && matrixArts.length > 0 && pages.length > 0) ? (
            <canvas ref={canvasRef} className="max-h-full max-w-full rounded shadow" style={{ objectFit: "contain" }} />
          ) : (
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          )}
        </div>
        {/* Sag: ayarlar, renkler, kaynak gorsel (damlalik) */}
        <div className="w-[440px] shrink-0 space-y-3 overflow-y-auto border-l p-4">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1">Stil
              <select value={style.key}
                onChange={(e) => { const st = CHART_STYLES.find((x) => x.key === e.target.value)!; setStyle(st); setTitle(st.title); }}
                className="rounded border border-gray-200 px-1 py-0.5">
                {CHART_STYLES.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-1">Başlık
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-36 rounded border border-gray-200 px-1.5 py-0.5" />
            </label>
            <label className="flex items-center gap-1">Zemin
              <input type="color" value={style.bgTop} title="Üst" onChange={(e) => setStyle((st) => ({ ...st, bgTop: e.target.value }))} className="h-6 w-7" />
              <input type="color" value={style.bgBottom} title="Alt" onChange={(e) => setStyle((st) => ({ ...st, bgBottom: e.target.value }))} className="h-6 w-7" />
            </label>
            <label className="flex items-center gap-1">Sütun
              <select value={columns} onChange={(e) => setColumns(Number(e.target.value))} className="rounded border border-gray-200 px-1 py-0.5">
                {[3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          {designs.length > 0 && (
            <div className="flex gap-1 rounded-lg bg-gray-100 p-0.5">
              <button onClick={() => setSource("colors")} disabled={colors.length === 0}
                className={`flex-1 rounded-md px-2 py-1 ${source === "colors" ? "bg-white font-medium shadow-sm" : "text-gray-500"} disabled:opacity-40`}>
                Renk seçeneklerinden (boya)
              </button>
              <button onClick={() => setSource("designs")}
                className={`flex-1 rounded-md px-2 py-1 ${source === "designs" ? "bg-white font-medium shadow-sm" : "text-gray-500"}`}>
                Oluşturulan tasarımlardan
              </button>
              <button onClick={() => setSource("matrix")}
                className={`flex-1 rounded-md px-2 py-1 ${source === "matrix" ? "bg-white font-medium shadow-sm" : "text-gray-500"}`}>
                Tasarım × renk
              </button>
            </div>
          )}
          {source === "matrix" && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-1">Yerleşim
                  <select value={layout} onChange={(e) => setLayout(e.target.value as "matrix" | "rows")} className="rounded border border-gray-200 px-1 py-0.5">
                    <option value="matrix">Matris (sütun = renk)</option>
                    <option value="rows">Renk başına satır</option>
                  </select>
                </label>
                <label className="flex items-center gap-1">Görsel başına renk
                  <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }} className="rounded border border-gray-200 px-1 py-0.5">
                    {[3, 4, 5, 6, 8, 10, 12].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={showHex} onChange={(e) => setShowHex(e.target.checked)} /> Hex kodları görselde
                </label>
              </div>
              {pages.length > 1 && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Sayfa:</span>
                  {pages.map((_, i) => (
                    <button key={i} onClick={() => setPage(i)}
                      className={`rounded px-2 py-0.5 ${page === i ? "bg-amber-600 text-white" : "bg-gray-100 text-gray-700"}`}>{i + 1}</button>
                  ))}
                  <span className="text-gray-400">({mColors.length} renk → {pages.length} görsel)</span>
                </div>
              )}
              <p className="font-medium text-gray-700">Renkler ({mColors.length})</p>
              <div className="space-y-1">
                {mColors.map((c, i) => (
                  <div key={i} className="flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-1">
                    <input type="color" value={c.hex || "#888888"}
                      onChange={(e) => updateM((p) => p.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))}
                      className="h-5 w-6 cursor-pointer border-0 p-0" />
                    {EyeDropper && (
                      <button title="Damlalık: ekrandan rengi al"
                        onClick={() => void (async () => { try { const r = await new EyeDropper().open(); updateM((p) => p.map((x, j) => (j === i ? { ...x, hex: r.sRGBHex } : x))); } catch { /* vazgecti */ } })()}
                        className="text-gray-400 hover:text-amber-600"><Pipette className="h-3 w-3" /></button>
                    )}
                    <input value={c.name}
                      onChange={(e) => updateM((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                      className="min-w-0 flex-1 rounded border border-gray-100 px-1 py-0.5" />
                    <input value={c.hex ?? ""} placeholder="#hex"
                      onChange={(e) => updateM((p) => p.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))}
                      className="w-20 rounded border border-gray-100 px-1 py-0.5 font-mono text-[10px]" />
                    <HexCopy hex={/^#[0-9a-f]{6}$/i.test(c.hex ?? "") ? c.hex : null} />
                    <button onClick={() => updateM((p) => { if (i === 0) return p; const n = [...p]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}
                      disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">↑</button>
                    <button onClick={() => updateM((p) => { if (i === p.length - 1) return p; const n = [...p]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; return n; })}
                      disabled={i === mColors.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">↓</button>
                    <button onClick={() => updateM((p) => p.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600">×</button>
                  </div>
                ))}
              </div>
              {/* Yeni renk: ad + hex (yaz/yapistir, secici ya da damlalik) */}
              <div className="flex items-center gap-1 rounded border border-dashed border-amber-300 bg-amber-50/50 px-1.5 py-1">
                <input type="color" value={/^#[0-9a-f]{6}$/i.test(newColor.hex) ? newColor.hex : "#888888"}
                  onChange={(e) => setNewColor((n) => ({ ...n, hex: e.target.value }))} className="h-5 w-6 cursor-pointer border-0 p-0" />
                {EyeDropper && (
                  <button title="Damlalık" onClick={() => void (async () => { try { const r = await new EyeDropper().open(); setNewColor((n) => ({ ...n, hex: r.sRGBHex })); } catch { /* vazgecti */ } })()}
                    className="text-gray-400 hover:text-amber-600"><Pipette className="h-3 w-3" /></button>
                )}
                <input value={newColor.name} placeholder="Renk adı (örn. NAVY)"
                  onChange={(e) => {
                    const name = e.target.value;
                    const known = inkHex(name);
                    setNewColor((n) => ({ name, hex: known ?? n.hex }));
                  }}
                  className="min-w-0 flex-1 rounded border border-gray-200 px-1 py-0.5" />
                <input value={newColor.hex} placeholder="#hex" onChange={(e) => setNewColor((n) => ({ ...n, hex: e.target.value.trim() }))}
                  className="w-20 rounded border border-gray-200 px-1 py-0.5 font-mono text-[10px]" />
                <button
                  onClick={() => {
                    const hex = newColor.hex.startsWith("#") ? newColor.hex : `#${newColor.hex}`;
                    if (!newColor.name.trim() || !/^#[0-9a-f]{6}$/i.test(hex)) return;
                    updateM((p) => [...p, { name: newColor.name.trim().toUpperCase(), hex }]);
                    setNewColor({ name: "", hex });
                  }}
                  disabled={!newColor.name.trim() || !/^#?[0-9a-f]{6}$/i.test(newColor.hex)}
                  className="rounded bg-amber-600 px-2 py-0.5 font-medium text-white hover:bg-amber-700 disabled:opacity-50">+ Ekle</button>
              </div>
              <p className="font-medium text-gray-700">Tasarımlar ({picked.length})</p>
            </div>
          )}
          {source === "designs" || source === "matrix" ? (
            <div className="space-y-1">
              <p className="text-gray-500">{source === "matrix" ? "Matrise girecek tasarımlar (işareti kaldırarak çıkar):" : "Kartelaya girecek tasarımlar ve altlarındaki yazı (işareti kaldırarak çıkar):"}</p>
              {designs.map((d) => {
                const p = picked.find((x) => x.id === d.id);
                return (
                  <label key={d.id} className="flex items-center gap-2 rounded border border-gray-200 bg-white p-1">
                    <input type="checkbox" checked={Boolean(p)}
                      onChange={(e) => setPicked((prev) => e.target.checked
                        ? [...prev, { id: d.id, label: d.label }]
                        : prev.filter((x) => x.id !== d.id))} />
                    <img src={d.url} alt="" className="h-9 w-9 rounded border border-gray-100 bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:8px_8px] object-contain" />
                    <input value={p?.label ?? d.label} disabled={!p}
                      onChange={(e) => setPicked((prev) => prev.map((x) => (x.id === d.id ? { ...x, label: e.target.value } : x)))}
                      className="min-w-0 flex-1 rounded border border-gray-200 px-1.5 py-0.5 disabled:bg-gray-50" />
                  </label>
                );
              })}
            </div>
          ) : (
          <div className="flex flex-wrap gap-1">
            {colors.map((c, i) => (
              <span key={i} className="flex items-center gap-1 rounded-full border border-gray-200 bg-white py-0.5 pl-1 pr-1.5">
                <input type="color" value={c.hex || "#888888"}
                  onChange={(e) => setColors((p) => p.map((x, j) => (j === i ? { ...x, hex: e.target.value } : x)))}
                  className="h-4 w-5 cursor-pointer border-0 p-0" />
                {EyeDropper && (
                  <button onClick={() => void pickFromScreen(i)} title="Damlalık: ekrandan (kaynak kartelada) rengi birebir al"
                    className="text-gray-400 hover:text-amber-600"><Pipette className="h-3 w-3" /></button>
                )}
                <input value={c.name}
                  onChange={(e) => setColors((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  className="w-20 bg-transparent" />
                <HexCopy hex={c.hex} />
                <button onClick={() => setColors((p) => p.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-600">×</button>
              </span>
            ))}
            <button onClick={() => setColors((p) => [...p, { name: "NEW", hex: "#888888" }])}
              className="rounded-full border border-dashed border-gray-300 px-2 text-gray-500">+ renk</button>
          </div>
          )}
          {referenceImages.length > 0 && (
            <div>
              <p className="mb-1 text-gray-500">
                Kaynak görsel — renk yanlışsa, rengin yanındaki damlalığa basıp buradaki kartelaya tıkla
                {!EyeDropper && " (damlalık bu tarayıcıda yok; Chrome/Edge kullanın)"}:
              </p>
              <div className="mb-1 flex gap-1">
                {referenceImages.map((u, i) => (
                  <button key={u} onClick={() => setRefIdx(i)}
                    className={`h-8 w-8 overflow-hidden rounded border ${refIdx === i ? "border-amber-500" : "border-gray-200"}`}>
                    <img src={u} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
              <img src={referenceImages[refIdx]} alt="" className="w-full rounded border border-gray-200 bg-white object-contain" />
            </div>
          )}
          <button
            onClick={saveAll}
            disabled={saving || (source === "matrix" ? matrixArts.length === 0 || pages.length === 0 : entries.length === 0 || (source === "colors" && !design))}
            className="flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Palette className="h-3.5 w-3.5" />}
            {source === "matrix" && pages.length > 1 ? `${pages.length} kartela görselini kaydet` : "Kartelayı kaydet"} (kütüphaneye de eklenir)
          </button>
        </div>
      </div>
    </div>
    </div>
  );
}
